import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Loader2, MapPin, CalendarDays } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { T } from '../theme/radar';

interface Evento {
  id: string;
  title: string | null;
  description: string | null;
  location: string | null;
  starts_at: string;
  ends_at: string | null;
  all_day: boolean;
  source: string;
  synced_at: string;
}

interface Props {
  clientId: string;
  clientName: string;
}

const MESI = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
const GIORNI = ['dom', 'lun', 'mar', 'mer', 'gio', 'ven', 'sab'];

// Riunioni ricorrenti del team del cliente (call settimanali, allineamenti su
// Teams): a chi scrive non servono, si vedono solo se lo chiede.
const isRiunione = (e: Evento) =>
  /\b(call|allineamento|riunione|meeting)\b/i.test(e.title || '') || /teams\.microsoft\.com/i.test(e.description || '');

const chiaveGiorno = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
const ora = (s: string) => new Date(s).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });

/**
 * Calendario del cliente, mese per mese.
 *
 * Sono gli eventi del calendario di team del cliente (serate, menu nuovi,
 * riprese, premi): chi scrive li vede qui e ci costruisce sopra post e storie.
 * Se il cliente ha l'indirizzo iCal si aggiornano da soli ogni 30 minuti.
 */
const CalendarioCliente: React.FC<Props> = ({ clientId, clientName }) => {
  const { getToken } = useAuth();
  const [mese, setMese] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [eventi, setEventi] = useState<Evento[]>([]);
  const [automatico, setAutomatico] = useState(false);
  const [inCaricamento, setInCaricamento] = useState(true);
  const [conRiunioni, setConRiunioni] = useState(false);

  const carica = useCallback(async () => {
    setInCaricamento(true);
    try {
      const to = new Date(mese.getFullYear(), mese.getMonth() + 1, 1);
      const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
      const res = await fetch(`/api/client-calendar?client_id=${clientId}&from=${fmt(mese)}&to=${fmt(to)}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (res.ok) {
        const data = (await res.json()).data;
        setEventi(data.eventi || []);
        setAutomatico(!!data.automatico);
      }
    } catch (error) {
      console.error('Errore nel caricamento del calendario:', error);
    } finally {
      setInCaricamento(false);
    }
  }, [clientId, mese, getToken]);

  useEffect(() => {
    carica();
  }, [carica]);

  const riunioni = useMemo(() => eventi.filter(isRiunione).length, [eventi]);

  const perGiorno = useMemo(() => {
    const visibili = conRiunioni ? eventi : eventi.filter((e) => !isRiunione(e));
    const gruppi = new Map<string, { data: Date; eventi: Evento[] }>();
    for (const e of visibili) {
      const d = new Date(e.starts_at);
      const k = chiaveGiorno(d);
      if (!gruppi.has(k)) gruppi.set(k, { data: d, eventi: [] });
      gruppi.get(k)!.eventi.push(e);
    }
    // In ogni giorno prima gli eventi di tutto il giorno, poi in ordine di ora
    gruppi.forEach((g) => {
      g.eventi.sort((a, b) => Number(b.all_day) - Number(a.all_day) || a.starts_at.localeCompare(b.starts_at));
    });
    return Array.from(gruppi.values());
  }, [eventi, conRiunioni]);

  const oggi = chiaveGiorno(new Date());
  const inizioOggi = new Date();
  inizioOggi.setHours(0, 0, 0, 0);
  const aggiornato = eventi[0]?.synced_at
    ? new Date(eventi[0].synced_at).toLocaleString('it-IT', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
    : null;

  const sposta = (n: number) => setMese((m) => new Date(m.getFullYear(), m.getMonth() + n, 1));

  return (
    <div className="max-w-3xl">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <button onClick={() => sposta(-1)} className="p-1.5 rounded-lg" style={{ border: `1px solid ${T.border}`, background: T.surface }} title="Mese prima">
            <ChevronLeft size={16} color={T.ink} />
          </button>
          <span className="text-base font-semibold min-w-[150px] text-center" style={{ color: T.ink }}>
            {MESI[mese.getMonth()]} {mese.getFullYear()}
          </span>
          <button onClick={() => sposta(1)} className="p-1.5 rounded-lg" style={{ border: `1px solid ${T.border}`, background: T.surface }} title="Mese dopo">
            <ChevronRight size={16} color={T.ink} />
          </button>
        </div>
        {riunioni > 0 && (
          <label className="flex items-center gap-2 text-xs cursor-pointer select-none" style={{ color: T.inkMuted }}>
            <input type="checkbox" checked={conRiunioni} onChange={(e) => setConRiunioni(e.target.checked)} />
            Mostra anche call e riunioni ({riunioni})
          </label>
        )}
      </div>

      {inCaricamento ? (
        <div className="flex items-center gap-2 py-10 justify-center text-sm" style={{ color: T.inkMuted }}>
          <Loader2 size={16} className="animate-spin" /> Carico il calendario
        </div>
      ) : perGiorno.length === 0 ? (
        <div className="py-10 text-center text-sm rounded-xl" style={{ color: T.inkMuted, background: T.surface, border: `1px solid ${T.borderSoft}` }}>
          Nessun evento nel calendario di {clientName} per {MESI[mese.getMonth()].toLowerCase()}.
        </div>
      ) : (
        <div className="space-y-2">
          {perGiorno.map(({ data, eventi: delGiorno }) => {
            const passato = data < inizioOggi;
            const eOggi = chiaveGiorno(data) === oggi;
            return (
              <div
                key={chiaveGiorno(data)}
                className="flex gap-3 rounded-xl px-3 py-2.5"
                style={{
                  background: T.surface,
                  border: `1px solid ${eOggi ? T.ink : T.borderSoft}`,
                  opacity: passato ? 0.55 : 1,
                }}
              >
                <div className="w-12 flex-shrink-0 text-center pt-0.5">
                  <div className="text-[11px] uppercase" style={{ color: T.inkFaint }}>{GIORNI[data.getDay()]}</div>
                  <div className="text-xl font-semibold leading-tight" style={{ color: T.ink }}>{data.getDate()}</div>
                  {eOggi && (
                    <div className="mt-0.5 text-[10px] font-semibold rounded px-1" style={{ background: T.accent, color: T.onAccent }}>oggi</div>
                  )}
                </div>
                <div className="flex-1 min-w-0 space-y-1.5">
                  {delGiorno.map((e) => (
                    <div key={e.id}>
                      <div className="flex items-baseline gap-2">
                        <span className="text-xs w-16 flex-shrink-0 tabular-nums" style={{ color: T.inkMuted }}>
                          {e.all_day ? 'tutto il giorno' : ora(e.starts_at)}
                        </span>
                        <span className="text-sm font-medium break-words" style={{ color: T.ink }}>{e.title || '(senza titolo)'}</span>
                      </div>
                      {(e.location || e.description) && (
                        <div className="pl-[4.5rem] text-xs space-y-0.5" style={{ color: T.inkMuted }}>
                          {e.location && (
                            <div className="flex items-center gap-1"><MapPin size={11} /> {e.location}</div>
                          )}
                          {e.description && <div className="whitespace-pre-line break-words">{e.description}</div>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <p className="mt-4 flex items-center gap-1.5 text-[11px]" style={{ color: T.inkFaint }}>
        <CalendarDays size={12} />
        {automatico
          ? `Calendario di ${clientName}, si aggiorna da solo ogni 30 minuti${aggiornato ? ` (ultimo: ${aggiornato})` : ''}.`
          : `Calendario di ${clientName}, copia del ${aggiornato || '…'}: non si aggiorna da solo finché non c'è l'indirizzo iCal.`}
      </p>
    </div>
  );
};

export default CalendarioCliente;
